import type React from "react"

import { useState, useEffect } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { teamsApi } from "@/lib/api/teams"
import { useTeam } from "@/lib/api/use-teams"
import { queryKeys } from "@/lib/api/query-keys"
import { ApiError } from "@/lib/api/client"
import { useOrganization } from "@/lib/organization/organization-context"
import { USStateSelect } from "@/components/ui/us-state-select"
import { formatPhone, isValidPhone } from "@/lib/utils/phone"
import { formatZip, isValidZip } from "@/lib/utils/zip"
import {
  Field,
  PrimaryButton,
  onboardingInputClass,
  onboardingInputErrorClass,
} from "@/components/onboarding/onboarding-shell"
import { cx } from "@/lib/utils/cx"

// Group → Business info. Identity-only (name, address, phone). Adding
// dealerships and managing the network lives in Group → Network
// (rendered by OrgChartPanel) — keeping each section single-purpose
// avoids duplication across surfaces.
interface GroupSettingsProps {
  groupId?: number | string | null
}

export function GroupSettings({ groupId: groupIdProp }: GroupSettingsProps = {}) {
  const ctx = useOrganization()
  const groupId = groupIdProp ?? ctx.activeGroupId
  const queryClient = useQueryClient()
  const { data: group, isLoading } = useTeam(groupId)

  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    name: "",
    street: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
  })

  useEffect(() => {
    if (!group) return
    setFormData({
      name: group.name || "",
      street: group.street || "",
      city: group.city || "",
      state: group.state || "",
      zip: group.zipCode ? formatZip(group.zipCode) : "",
      phone: group.phoneNumber ? formatPhone(group.phoneNumber) : "",
    })
  }, [group])

  const phoneValid = !formData.phone || isValidPhone(formData.phone)
  const zipValid = !formData.zip || isValidZip(formData.zip)
  const canSave = phoneValid && zipValid

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!groupId) return
    setSaving(true)
    try {
      const id = Number(groupId)
      const tasks: Promise<unknown>[] = []
      if (formData.name && formData.name !== group?.name) {
        tasks.push(teamsApi.update(id, { name: formData.name }))
      }
      tasks.push(
        teamsApi.updateSettings(id, {
          street: formData.street || undefined,
          city: formData.city || undefined,
          state: formData.state || undefined,
          zipCode: formData.zip || undefined,
          phoneNumber: formData.phone || undefined,
        }),
      )
      await Promise.all(tasks)
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.detail(groupId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.all })
      toast.success("Group details updated")
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Failed to update group details"
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  if (!groupId) {
    return <p className="text-[13px] text-zinc-500">No group selected.</p>
  }

  return (
    <div>
      {isLoading ? (
        <p className="text-sm text-zinc-400">Loading…</p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <Field label="Group name">
            <input
              id="group-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Auto Group Inc."
              className={onboardingInputClass}
            />
          </Field>

          <Field label="Street">
            <input
              id="group-street"
              value={formData.street}
              onChange={(e) => setFormData({ ...formData, street: e.target.value })}
              placeholder="123 Main Street"
              className={onboardingInputClass}
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-[1.4fr_1fr] gap-3">
            <Field label="City">
              <input
                id="group-city"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder="Springfield"
                className={onboardingInputClass}
              />
            </Field>
            <Field label="State">
              <USStateSelect
                id="group-state"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                className={onboardingInputClass}
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field
              label="ZIP code"
              error={!zipValid ? "Please enter a valid ZIP code" : undefined}
            >
              <input
                id="group-zip"
                inputMode="numeric"
                value={formData.zip}
                onChange={(e) => setFormData({ ...formData, zip: formatZip(e.target.value) })}
                placeholder="62701"
                maxLength={10}
                aria-invalid={!zipValid || undefined}
                className={cx(onboardingInputClass, !zipValid && onboardingInputErrorClass)}
              />
            </Field>
            <Field
              label="Phone"
              error={!phoneValid ? "Please enter a valid phone number" : undefined}
            >
              <input
                id="group-phone"
                type="tel"
                inputMode="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: formatPhone(e.target.value) })}
                placeholder="(555) 123-4567"
                maxLength={20}
                aria-invalid={!phoneValid || undefined}
                className={cx(onboardingInputClass, !phoneValid && onboardingInputErrorClass)}
              />
            </Field>
          </div>

          <div className="pt-2">
            <PrimaryButton loading={saving} disabled={!canSave}>
              {saving ? "Saving…" : "Save changes"}
            </PrimaryButton>
          </div>
        </form>
      )}
    </div>
  )
}
